/**
 * UniFi Network List Page
 * ========================
 * Browse and manage UniFi network devices (switches, APs, gateways).
 * Filtered to infrastructure_source='unifi' and uses UniFi-specific discovery.
 */

import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams as useRouterSearchParams, useNavigate } from 'react-router-dom'
import { Plus, Search, Network, Wifi, Shield, Router, BatteryCharging, Camera, Bell, Volume2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import QueryGatewayModal from '@/components/QueryGatewayModal'
import { useAssets } from '@/hooks/useAssets'
import { useBatchResync } from '@/hooks/useBatchResync'
import { useAssetLocations } from '@/hooks/useAssetLocations'
import type { AssetSearchParams, Asset } from '@/types'
import { ASSET_STATUS_COLORS, ASSET_STATUS_LABELS } from '@/types'

const DEVICE_TYPE_ICONS: Record<string, React.ReactNode> = {
  switch: <Network size={16} className="text-blue-500" />,
  access_point: <Wifi size={16} className="text-green-500" />,
  firewall: <Shield size={16} className="text-purple-500" />,
  router: <Router size={16} className="text-orange-500" />,
  ups: <BatteryCharging size={16} className="text-yellow-500" />,
  camera: <Camera size={16} className="text-red-500" />,
  doorbell: <Bell size={16} className="text-red-400" />,
  chime: <Volume2 size={16} className="text-pink-500" />,
}

const DEVICE_TYPE_LABELS: Record<string, string> = {
  switch: 'Switch',
  access_point: 'AP',
  firewall: 'Gateway',
  router: 'Router',
  ups: 'UPS',
  camera: 'Camera',
  doorbell: 'Doorbell',
  chime: 'Chime',
  other: 'Other',
}

/** Format port/connection summary for all UniFi device types */
function formatPortSummary(asset: Asset): string | null {
  const { asset_type, notes, network_ports, status } = asset

  // Switches: "24 ports / 18 active / 12 PoE"
  if (asset_type === 'switch' && notes) {
    const match = notes.match(/Switch Ports: (\d+) total, (\d+) active/)
    if (match) {
      const parts = [`${match[1]} ports`, `${match[2]} active`]
      const poeMatch = notes.match(/PoE: (\d+) ports/)
      if (poeMatch) parts.push(`${poeMatch[1]} PoE`)
      return parts.join(' / ')
    }
  }

  // Gateways/Firewalls: "11 ports / 2 active"
  if ((asset_type === 'firewall' || asset_type === 'router') && notes) {
    const match = notes.match(/Ports: (\d+) total, (\d+) active/)
    if (match) {
      const parts = [`${match[1]} ports`, `${match[2]} active`]
      const poeMatch = notes.match(/PoE: (\d+) ports/)
      if (poeMatch) parts.push(`${poeMatch[1]} PoE`)
      return parts.join(' / ')
    }
  }

  // Access Points: show uplink info or just "Connected" status with speed
  if (asset_type === 'access_point') {
    if (notes) {
      const uplinkMatch = notes.match(/Uplink: (.+?)(?:\n|$)/)
      if (uplinkMatch) return uplinkMatch[1]
    }
    // Show connected status with speed if available
    if (status === 'online') {
      return 'Connected'
    }
    return null
  }

  // Cameras/Doorbells: "1 port" if online
  if (asset_type === 'camera' || asset_type === 'doorbell') {
    if (status === 'online') return '1 port'
    return null
  }

  // UPS: show outlet count if available in notes
  if (asset_type === 'ups' && notes) {
    const outletMatch = notes.match(/Outlets: (\d+)/)
    if (outletMatch) return `${outletMatch[1]} outlets`
  }

  // Chimes: no port info needed
  if (asset_type === 'chime') return null

  // Fallback to network_ports for any type
  if (network_ports) {
    return `${network_ports} ports`
  }

  return null
}

export default function UniFiList() {
  const navigate = useNavigate()
  const [routerSearchParams] = useRouterSearchParams()
  const initialSearch = routerSearchParams.get('search') || ''

  // Filter by infrastructure_source='unifi'
  const [searchParams, setSearchParams] = useState<AssetSearchParams>({
    page: 1,
    limit: 20,
    search: initialSearch,
    infrastructure_source: 'unifi',
  })

  // Modal state
  const [showQueryModal, setShowQueryModal] = useState(false)

  // Autocomplete state
  const [searchInput, setSearchInput] = useState(initialSearch)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchRef = useRef<HTMLDivElement>(null)

  // Update search when URL changes
  useEffect(() => {
    const urlSearch = routerSearchParams.get('search') || ''
    setSearchParams((prev) => ({
      ...prev,
      search: urlSearch,
      page: 1,
      infrastructure_source: 'unifi',
    }))
    setSearchInput(urlSearch)
  }, [routerSearchParams])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { data, isLoading, error } = useAssets(searchParams)
  const { data: locationsData } = useAssetLocations()

  // Autocomplete search results (filtered to UniFi)
  const searchTerm = searchInput.trim()
  const { data: autocompleteResults, isLoading: autocompleteLoading } = useAssets(
    { search: searchTerm, limit: 8, infrastructure_source: 'unifi' },
    { enabled: searchTerm.length >= 2 }
  )
  const suggestions = searchTerm.length >= 2 ? (autocompleteResults?.data ?? []) : []

  const assets = data?.data ?? []
  const meta = data?.meta
  const locations = locationsData?.data ?? []

  // Batch resync: auto-sync all assets on page load
  const { syncingIds, isSyncing, completedCount, totalCount, startBatchResync } = useBatchResync(searchParams)
  const hasTriggeredResyncRef = useRef(false)
  useEffect(() => {
    if (!isLoading && assets.length > 0 && !hasTriggeredResyncRef.current) {
      hasTriggeredResyncRef.current = true
      startBatchResync(assets)
    }
  }, [isLoading, assets.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setShowDropdown(false)
    setSearchParams((prev) => ({
      ...prev,
      search: searchInput,
      page: 1,
    }))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    setShowDropdown(true)
    setSelectedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      const asset = suggestions[selectedIndex]
      setShowDropdown(false)
      navigate(`/assets/${asset.asset_id}`)
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleSuggestionClick = (assetId: string) => {
    setShowDropdown(false)
    navigate(`/assets/${assetId}`)
  }

  const handleFilterChange = (key: keyof AssetSearchParams, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }))
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
  }

  // Get icon for asset type
  const getDeviceIcon = (asset: Asset) => {
    return DEVICE_TYPE_ICONS[asset.asset_type] || <Network size={16} className="text-gray-500" />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading assets</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Network className="text-blue-500" size={28} />
            UniFi Network
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {meta?.total ?? 0} network devices tracked
          </p>
          {isSyncing && (
            <p className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1.5 mt-0.5">
              <Loader2 size={14} className="animate-spin" />
              Syncing {completedCount}/{totalCount} assets...
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={<Network size={16} />}
            onClick={() => setShowQueryModal(true)}
          >
            Query Gateway
          </Button>
          <Link to="/assets/new">
            <Button icon={<Plus size={16} />}>Add Manually</Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]" ref={searchRef}>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  name="search"
                  type="text"
                  placeholder="Search by name, IP, MAC, model..."
                  value={searchInput}
                  onChange={handleInputChange}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
                {autocompleteLoading && searchTerm.length >= 2 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                {/* Autocomplete dropdown */}
                {showDropdown && searchTerm.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                    {suggestions.length === 0 && !autocompleteLoading ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No UniFi devices found for "{searchInput}"
                      </div>
                    ) : (
                      suggestions.map((asset, index) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => handleSuggestionClick(asset.asset_id)}
                          className={`w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            index === selectedIndex ? 'bg-primary-50 dark:bg-primary-900/50' : ''
                          } ${index !== suggestions.length - 1 ? 'border-b dark:border-gray-700' : ''}`}
                        >
                          <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                            {getDeviceIcon(asset)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {asset.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {asset.asset_id}
                              {asset.ip_address && ` \u2022 ${asset.ip_address}`}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${ASSET_STATUS_COLORS[asset.status]}`}>
                            {ASSET_STATUS_LABELS[asset.status]}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.status ?? ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.asset_type ?? ''}
              onChange={(e) => handleFilterChange('asset_type', e.target.value)}
            >
              <option value="">All Types</option>
              {Object.entries(DEVICE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.location_id ?? ''}
              onChange={(e) => handleFilterChange('location_id', e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Assets list */}
      <Card>
        <CardHeader title="UniFi Devices" />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ports
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Network
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Firmware
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : assets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <Network className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
                    <p className="text-gray-500 dark:text-gray-400">No UniFi devices found</p>
                    <button
                      onClick={() => setShowQueryModal(true)}
                      className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
                    >
                      Query gateway to discover devices
                    </button>
                  </td>
                </tr>
              ) : (
                assets.map((asset) => {
                  const portSummary = formatPortSummary(asset)

                  return (
                    <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {/* Device */}
                      <td className="px-6 py-4">
                        <Link
                          to={`/assets/${asset.asset_id}`}
                          className="flex items-center gap-3"
                        >
                          {asset.manufacturer?.logo_url ? (
                            <img
                              src={asset.manufacturer.logo_url}
                              alt={asset.manufacturer.name}
                              className="w-10 h-10 rounded-lg object-contain"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                              {getDeviceIcon(asset)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {asset.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {asset.model || 'UniFi Device'}
                            </p>
                          </div>
                        </Link>
                      </td>

                      {/* Type */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(asset)}
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {DEVICE_TYPE_LABELS[asset.asset_type] || asset.asset_type}
                          </span>
                        </div>
                      </td>

                      {/* Ports */}
                      <td className="px-6 py-4">
                        {portSummary ? (
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {portSummary}
                          </p>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>

                      {/* Network */}
                      <td className="px-6 py-4">
                        {asset.ip_address ? (
                          <p className="text-sm font-mono text-gray-900 dark:text-white">
                            {asset.ip_address}
                          </p>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                        {asset.mac_address && (
                          <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                            {asset.mac_address}
                          </p>
                        )}
                      </td>

                      {/* Firmware */}
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          {asset.firmware_version && (
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {asset.firmware_version}
                            </span>
                          )}
                          {asset.firmware_update_available === true ? (
                            <span
                              className="w-2.5 h-2.5 bg-red-500 rounded-full"
                              title={`Update available: ${asset.firmware_update_version || 'newer version'}`}
                            />
                          ) : asset.firmware_update_available === false ? (
                            <span
                              className="w-2.5 h-2.5 bg-green-500 rounded-full"
                              title="Firmware is up to date"
                            />
                          ) : (
                            <span
                              className="w-2.5 h-2.5 bg-gray-400 rounded-full"
                              title="Update status unknown"
                            />
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 text-center">
                        {syncingIds.has(asset.id) ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                            <Loader2 size={12} className="animate-spin" />
                            Syncing
                          </span>
                        ) : (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${ASSET_STATUS_COLORS[asset.status]}`}>
                            {ASSET_STATUS_LABELS[asset.status]}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Query Gateway Modal */}
      <QueryGatewayModal
        isOpen={showQueryModal}
        onClose={() => setShowQueryModal(false)}
      />
    </div>
  )
}
